import { defineConfig } from '@chakra-ui/react';

const baseSemanticColors = {
  text: {
    primary: {
      value: { base: '{colors.gray.600}', _dark: '#C9C8C7' },
    },
    secondary: {
      value: { base: '#6B6A69', _dark: '#8F8E8C' },
    },
    placeholder: {
      value: { base: '{colors.gray.400}', _dark: '#61605F' },
    },
    selected: {
      value: { base: '{colors.selection.500}', _dark: '{colors.selection.500}' },
    },
    error: {
      value: { base: '{colors.error.500}', _dark: '{colors.error.500}' },
    },
    inverse: {
      value: { base: '{colors.white}', _dark: '#1C1C1B' },
    },
  },
  surface: {
    background: {
      value: { base: '{colors.white}', _dark: '#1C1C1B' },
    },
    hover: {
      value: { base: '{colors.gray.100}', _dark: '#2E2E2D' },
    },
    muted: {
      value: { base: '{colors.gray.50}', _dark: '#242423' },
    },
    accent: {
      value: { base: '{colors.gray.200}', _dark: '#363635' },
    },
    accentStrong: {
      value: { base: '{colors.gray.300}', _dark: '#40403F' },
    },
    inverse: {
      value: { base: '{colors.gray.800}', _dark: '#FFFFFF' },
    },
  },
  border: {
    light: {
      value: { base: '{colors.gray.200}', _dark: '#363635' },
    },
    dark: {
      value: { base: '{colors.gray.400}', _dark: '#61605F' },
    },
    inverse: {
      value: { base: '{colors.white}', _dark: '#1C1C1B' },
    },
  },
};

const config = defineConfig({
  theme: {
    tokens: {
      colors: {
        white: {
          value: '#FFFFFF',
        },
        black: {
          value: '#1C1C1B',
        },
        gray: {
          50: { value: '#F8F7F6' },
          100: { value: '#F5F4F2' },
          200: { value: '#E9E8E6' },
          300: { value: '#DDDCDB' },
          400: { value: '#BBBAB9' },
          500: { value: '#A1A1A0' },
          600: { value: '#535352' },
          700: { value: '#272726' },
          800: { value: '#171716' },
        },
        selection: {
          100: { value: '#F5FBFF' },
          200: { value: '#DDF1FE' },
          300: { value: '#C2E6FE' },
          400: { value: '#90D2FE' },
          500: { value: '#18A0FB' },
          600: { value: '#097FCE' },
        },
        error: {
          100: { value: '#FFF6F5' },
          200: { value: '#FFE4E1' },
          300: { value: '#FEC5BD' },
          400: { value: '#FD998B' },
          500: { value: '#FA5742' },
          600: { value: '#C8311E' },
        },
      },
    },
    semanticTokens: {
      colors: {
        surface: baseSemanticColors.surface,
        text: baseSemanticColors.text,
        border: baseSemanticColors.border,
        sidebar: {
          background: baseSemanticColors.surface.muted,
          hover: baseSemanticColors.surface.accent,
          hoverButton: baseSemanticColors.surface.accentStrong,
        },
        icon: {
          primary: {
            value: { base: '{colors.gray.600}', _dark: '#C9C8C7' },
          },
          secondary: {
            value: { base: '#6B6A69', _dark: '#8F8E8C' },
          },
          tertiary: {
            value: { base: '{colors.gray.400}', _dark: '#61605F' },
          },
          selected: {
            value: { base: '{colors.selection.500}', _dark: '{colors.selection.500}' },
          },
          error: {
            value: { base: '{colors.error.500}', _dark: '{colors.error.500}' },
          },
          inverse: {
            value: { base: '{colors.white}', _dark: '#1C1C1B' },
          },
        },
        chart: {
          accent: {
            value: { base: '{colors.selection.600}', _dark: '{colors.selection.500}' },
          },
          // De-emphasis fill for the "context" half of an emphasis chart. One
          // value for both modes: it clears 3:1 on either surface.
          muted: {
            value: { base: '#8F8E8C', _dark: '#8F8E8C' },
          },
          grid: {
            value: { base: '{colors.gray.200}', _dark: '#363635' },
          },
          axis: {
            value: { base: '{colors.gray.400}', _dark: '#61605F' },
          },
        },
        // For a signed change beside a figure. The sign carries the meaning;
        // colour only reinforces it. Both clear 4.5:1 as text on their surface.
        status: {
          positive: {
            value: { base: '#006300', _dark: '#0CA30C' },
          },
          negative: {
            value: { base: '{colors.error.600}', _dark: '{colors.error.500}' },
          },
        },
        tooltip: {
          background: {
            value: baseSemanticColors.surface.inverse,
          },
          color: {
            value: baseSemanticColors.text.inverse,
          },
        },
        input: {
          background: {
            value: { base: '{colors.gray.100}', _dark: '#2E2E2D' },
          },
          backgroundDisabled: {
            value: { base: '{colors.gray.100}', _dark: '#2E2E2D' },
          },
          border: {
            value: baseSemanticColors.border.light,
          },
          borderHover: {
            value: { base: '{colors.selection.400}', _dark: '#0E5E94' },
          },
          borderFocus: {
            value: { base: '{colors.selection.500}', _dark: '#18A0FB' },
          },
          color: {
            value: baseSemanticColors.text.primary,
          },
        },
        button: {
          primary: {
            background: {
              value: { base: '{colors.selection.100}', _dark: '#182430' },
            },
            border: {
              value: { base: '{colors.selection.300}', _dark: '#043454' },
            },
            color: baseSemanticColors.text.selected,
            hover: {
              value: { base: '{colors.selection.200}', _dark: '#10273B' },
            },
          },
          secondary: {
            background: baseSemanticColors.surface.background,
            border: {
              value: baseSemanticColors.border.light,
            },
            color: {
              value: baseSemanticColors.text.primary,
            },
            hover: {
              value: { base: '{colors.gray.100}', _dark: '#2E2E2D' },
            },
          },
          error: {
            background: {
              value: { base: '{colors.error.100}', _dark: '#300C07' },
            },
            border: {
              value: { base: '{colors.error.300}', _dark: '#54150D' },
            },
            color: {
              value: baseSemanticColors.text.error,
            },
            hover: {
              value: { base: '{colors.error.200}', _dark: '#45120B' },
            },
          },
        },
      },
    },
  },
});

export default config;
